/**
 * How a PDF output version describes itself to the user (R3, plan §1.1):
 *
 *   "24 pages"                            — a response PDF, nothing substituted
 *   "24 pages · Aptos shown as Carlito"   — a response PDF with a stand-in font
 *   "about 12 pages"                      — a buyer form (always "about", plan §9)
 *
 * A response PDF's count is measured from the PDF itself, so it is shown
 * plainly even when `pageCountApprox` is true (a stand-in font may move page
 * breaks against Word — that flag is for page-limit checks, not this line).
 * Only buyer forms say "about": their layout in Word differs from the PDF.
 */

import type { OutputVersion } from '@/types/output-version'

/** "Aptos → Carlito" (the converter's form) → "Aptos shown as Carlito". */
export function substitutionText(substitution: string): string {
  const [requested, standIn] = substitution.split('→').map((part) => part.trim())
  return requested && standIn ? `${requested} shown as ${standIn}` : substitution.trim()
}

export function pageCountText(pageCount: number, approx: boolean): string {
  const pages = `${pageCount} ${pageCount === 1 ? 'page' : 'pages'}`
  return approx ? `about ${pages}` : pages
}

/** The result line for a ready PDF; empty when there is nothing to say. */
export function pdfResultLine(version: Pick<OutputVersion, 'kind' | 'pageCount' | 'substitutedFonts'>): string {
  const parts: string[] = []
  if (version.pageCount !== null) parts.push(pageCountText(version.pageCount, version.kind === 'form_pdf'))
  if (version.substitutedFonts.length > 0) parts.push(version.substitutedFonts.map(substitutionText).join(', '))
  return parts.join(' · ')
}

/**
 * A failure message without its own "Try again" — the line offers the action
 * itself, so the words would say it twice.
 */
export function withoutTryAgain(message: string): string {
  const trimmed = message.replace(/\s*(please\s+)?try again\.?\s*$/i, '').trim()
  return trimmed || message
}
